import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  query,
  where,
  Timestamp,
} from "firebase/firestore";
import { db } from "../../firebase";
import { useVenue } from "../VenueProvider";
import { backendFetch } from "../../utils/backend";
import { Section, ACCENT } from "../cuentas/shared";
import { Kpi, haceEnDias } from "./shared";
import { usoPorUsuario } from "../../utils/usoPlataforma";
import { ENTITY_LABELS } from "../../utils/bitacora";
import {
  UsersIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/20/solid";

// ¿Tu equipo está usando el panel? Un renglón por persona con acceso a ESTE
// venue: en qué secciones trabaja, cuánto, y cuándo entró por última vez.
//
// Dos señales, porque una sola miente: las HORAS salen de `userActivity`
// (un doc por persona y hora, así que cuentan el rato con el panel abierto
// aunque no se toque nada — el visor que entra a ver el calendario) y las
// ACCIONES de la bitácora del venue (el uso que dejó consecuencias).
//
// El padrón sale de `users` filtrando por este venue, y es lo que permite
// listar a quien NO ha entrado: de esa persona no hay un solo rastro del
// cual deducirla. Las tres lecturas van por separado y cada una degrada
// sola: si las reglas nuevas todavía no están desplegadas, el reporte se
// pinta con lo que sí pudo leer y lo dice.
const VENTANAS = [
  { key: "30", label: "30 días", dias: 30 },
  { key: "60", label: "60 días", dias: 60 },
  { key: "90", label: "90 días", dias: 90 },
];

const ESTADO_STYLE = {
  activo: { label: "Activo", className: "text-emerald-600" },
  tibio: { label: "Intermitente", className: "text-amber-600" },
  inactivo: { label: "Inactivo", className: "text-red-600" },
  nunca: { label: "No entró", className: "text-zinc-400" },
};

// Sin la señal de conexión, la bitácora sola NO puede decir "no entró":
// alguien pudo pasarse la semana consultando el calendario sin capturar
// nada. Decirlo igual sería inventar un dato — se dice lo único que consta.
const SOLO_ACCIONES = {
  si: { label: "Capturó", className: "text-emerald-600" },
  no: { label: "Sin capturar", className: "text-zinc-400" },
};

const ROL_LABEL = { owner: "Owner", viewer: "Visor", admin: "Administrador" };

const fmtUltima = (fecha) =>
  fecha
    ? fecha.toLocaleString("es-MX", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const ReporteUsoVenue = () => {
  const { selectedVenueId, selectedVenue } = useVenue();
  const [ventana, setVentana] = useState("30");
  const [datos, setDatos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  const dias = VENTANAS.find((v) => v.key === ventana)?.dias ?? 30;

  const cargar = useCallback(async () => {
    if (!selectedVenueId) return;
    setLoading(true);
    const desde = Timestamp.fromDate(new Date(Date.now() - dias * 86400000));
    const fallos = [];

    // Bitácora del venue: esta lectura siempre estuvo permitida para el owner.
    let bitacora = [];
    try {
      const snap = await getDocs(
        query(
          collection(db, "venues", selectedVenueId, "bitacora"),
          where("timestamp", ">=", desde)
        )
      );
      bitacora = snap.docs.map((d) => ({ ...d.data(), venueId: selectedVenueId }));
    } catch (err) {
      console.error("Uso del panel · bitácora:", err);
      fallos.push({ que: "acciones", code: err?.code, msg: err?.message });
    }

    // Presencia. La consulta natural (venueId + lastSeen) necesita un índice
    // compuesto; mientras no exista, Firestore responde failed-precondition
    // con el link para crearlo. En vez de quedarnos sin el dato —que es el
    // que distingue "no entró" de "entró y no capturó"— se reintenta
    // filtrando solo por venue y se acota la ventana en memoria: más caro en
    // lecturas, pero el reporte dice la verdad desde hoy.
    let actividad = null;
    const desdeMs = desde.toDate().getTime();
    const enVentana = (d) => {
      const v = d?.lastSeen;
      const t = v?.toDate ? v.toDate().getTime() : v?.seconds ? v.seconds * 1000 : 0;
      return t >= desdeMs;
    };
    try {
      const snap = await getDocs(
        query(
          collection(db, "userActivity"),
          where("venueId", "==", selectedVenueId),
          where("lastSeen", ">=", desde)
        )
      );
      actividad = snap.docs.map((d) => d.data());
    } catch (err) {
      if (err?.code === "failed-precondition") {
        // El reintento se traga el error, así que el link para crear el
        // índice —que Firestore mete dentro del mensaje— hay que loggearlo
        // a mano o no llega a ninguna parte.
        console.warn(
          "Uso del panel · falta el índice userActivity(venueId + lastSeen). " +
            "Crear aquí:",
          err.message
        );
        try {
          const snap = await getDocs(
            query(
              collection(db, "userActivity"),
              where("venueId", "==", selectedVenueId)
            )
          );
          actividad = snap.docs.map((d) => d.data()).filter(enVentana);
        } catch (err2) {
          console.error("Uso del panel · presencia (sin índice):", err2);
          fallos.push({ que: "horas", code: err2?.code, msg: err2?.message });
        }
      } else {
        console.error("Uso del panel · presencia:", err);
        fallos.push({ que: "horas", code: err?.code, msg: err?.message });
      }
    }

    // Padrón del equipo: por el BACKEND, no por Firestore. Una regla
    // autoriza documentos completos y no campos, así que leer `users` desde
    // aquí le entregaba al owner el teléfono y la lista de venues de cada
    // compañero. Flask devuelve solo nombre, correo y rol.
    let roster = null;
    try {
      const data = await backendFetch(`/venues/${selectedVenueId}/team`);
      roster = Array.isArray(data?.team) ? data.team : [];
    } catch (err) {
      console.error("Uso del panel · equipo:", err);
      fallos.push({ que: "equipo", code: err?.status, msg: err?.message });
    }

    // Se sella con el venue al que pertenece: si el owner cambió de venue
    // mientras la consulta viajaba, esta respuesta ya no aplica.
    setDatos({ bitacora, actividad, roster, fallos, venueId: selectedVenueId });
    setLoading(false);
  }, [selectedVenueId, dias]);

  useEffect(() => {
    cargar();
  }, [cargar, tick]);

  // Nunca se pinta lo cargado para OTRO venue: al cambiar de venue el
  // estado anterior sigue en memoria hasta que llega la nueva respuesta, y
  // sin este sello se alcanzaba a ver al equipo del venue anterior.
  const datosVigentes = datos?.venueId === selectedVenueId ? datos : null;

  const uso = useMemo(() => {
    if (!datosVigentes) return null;
    return usoPorUsuario({
      // Sin padrón, las filas salen solo del rastro de actividad: se ve a
      // quien entró, nunca a quien no.
      users: datosVigentes.roster || [],
      actividad: datosVigentes.actividad || [],
      bitacora: datosVigentes.bitacora,
      venues: selectedVenue ? [{ id: selectedVenueId, ...selectedVenue }] : [],
    });
  }, [datosVigentes, selectedVenue, selectedVenueId]);

  const sinHoras = !datosVigentes?.actividad;
  const sinRoster = !datosVigentes?.roster;
  const permisoFaltante = (datosVigentes?.fallos || []).some(
    (f) => f.code === "permission-denied"
  );

  if (loading && !uso) {
    return (
      <Section icon={UsersIcon} title="Uso del panel">
        <p className="text-sm text-zinc-400">Cargando la actividad del equipo…</p>
      </Section>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-2">
        {VENTANAS.map((v) => (
          <button
            key={v.key}
            type="button"
            onClick={() => setVentana(v.key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
              ventana === v.key
                ? "text-white shadow-sm"
                : "text-zinc-500 bg-zinc-100 dark:bg-zinc-700/60 hover:text-zinc-700"
            }`}
            style={ventana === v.key ? { backgroundColor: ACCENT } : undefined}
          >
            {v.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setTick((t) => t + 1)}
          disabled={loading}
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-zinc-600 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
        >
          <ArrowPathIcon className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          Actualizar
        </button>
      </div>

      {datosVigentes?.fallos?.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/10 p-4">
          <div className="flex gap-2.5 text-sm text-amber-800 dark:text-amber-200">
            <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-amber-600" />
            <div className="space-y-1">
              {sinHoras && (
                <p>
                  <span className="font-semibold">
                    Falta la señal de conexión.
                  </span>{" "}
                  Sin ella solo se ve quién capturó algo, no quién entró: esta
                  tabla no puede afirmar que alguien no haya entrado.
                  {permisoFaltante && " Es el permiso: faltan desplegar las reglas de Firestore."}
                </p>
              )}
              {sinRoster && (
                <p>
                  <span className="font-semibold">Falta el equipo del venue.</span>{" "}
                  Solo aparece quien tuvo actividad; a quien no ha entrado no
                  hay de dónde sacarlo. La lista la sirve el backend, así que
                  o no respondió o todavía no tiene el endpoint desplegado.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi
          label="Equipo"
          value={uso.totales.medibles}
          hint={sinRoster ? "solo los que tuvieron actividad" : "con acceso a este venue"}
        />
        <Kpi
          label={sinHoras ? "Capturaron" : "Activos"}
          value={
            sinHoras
              ? uso.filas.filter((f) => f.acciones > 0).length
              : uso.totales.activos
          }
          hint={sinHoras ? "dejaron acciones en la ventana" : "entraron en los últimos 7 días"}
          tone="text-emerald-600"
        />
        <Kpi
          label={sinHoras ? "Sin capturar" : "Sin entrar"}
          value={
            sinHoras
              ? uso.filas.filter((f) => f.acciones === 0).length
              : uso.totales.inactivos + uso.totales.nunca
          }
          hint={
            sinHoras
              ? "pueden haber entrado sin tocar nada"
              : `más de 30 días, en la ventana de ${dias}`
          }
          tone="text-amber-600"
        />
        <Kpi
          label="Acciones"
          value={uso.totales.acciones}
          hint={sinHoras ? "registradas en la bitácora" : `en ${uso.totales.horas} horas de uso`}
        />
      </div>

      <Section icon={UsersIcon} title="Persona por persona">
        {uso.filas.length === 0 ? (
          <p className="text-sm text-zinc-400">
            Nadie registró actividad en esta ventana.
          </p>
        ) : (
          <div className="overflow-x-auto -mx-1">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-zinc-400">
                  <th className="py-2 pl-1 font-semibold">Persona</th>
                  <th className="py-2 font-semibold">Rol</th>
                  <th className="py-2 font-semibold">Actividad principal</th>
                  {!sinHoras && (
                    <th className="py-2 font-semibold text-right">Horas</th>
                  )}
                  <th className="py-2 font-semibold text-right">Acciones</th>
                  <th className="py-2 pr-1 font-semibold text-right">
                    {sinHoras ? "Última acción" : "Última vez"}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100 dark:divide-zinc-700/50">
                {uso.filas.map((f) => {
                  const estado = sinHoras
                    ? SOLO_ACCIONES[f.acciones > 0 ? "si" : "no"]
                    : ESTADO_STYLE[f.estado];
                  return (
                    <tr key={f.id}>
                      <td className="py-2 pl-1">
                        <div className="font-medium text-zinc-900 dark:text-white truncate max-w-[200px]">
                          {f.nombre || f.correo || "Sin nombre"}
                        </div>
                        {f.correo && (
                          <div className="text-xs text-zinc-400 truncate max-w-[200px]">
                            {f.correo}
                          </div>
                        )}
                      </td>
                      <td className="py-2 text-xs text-zinc-500 whitespace-nowrap">
                        {ROL_LABEL[f.rol] || f.rol || "—"}
                      </td>
                      <td className="py-2 text-xs text-zinc-500">
                        {f.actividadPrincipal.length > 0
                          ? f.actividadPrincipal
                              .map(
                                (a) =>
                                  `${ENTITY_LABELS[a.familia] || a.familia} (${a.total})`
                              )
                              .join(" · ")
                          : "—"}
                      </td>
                      {!sinHoras && (
                        <td className="py-2 text-right text-zinc-500 whitespace-nowrap">
                          {f.horas || "—"}
                        </td>
                      )}
                      <td className="py-2 text-right text-zinc-500 whitespace-nowrap">
                        {f.acciones || "—"}
                      </td>
                      <td className="py-2 pr-1 text-right whitespace-nowrap">
                        <div className={`font-medium ${estado.className}`}>
                          {estado.label}
                        </div>
                        <div className="text-xs text-zinc-400">
                          {f.ultima
                            ? `${fmtUltima(f.ultima)} · ${haceEnDias(f.ultima)}`
                            : sinHoras
                            ? `sin acciones en ${dias} días`
                            : `sin señal en ${dias} días`}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-3 text-xs text-zinc-400">
          Las horas cuentan los bloques de una hora con el panel abierto,
          aunque no se toque nada; las acciones son lo que quedó en la
          bitácora de este venue. El equipo de Venueboard no aparece: su
          actividad no se registra en tu venue.
        </p>
      </Section>
    </div>
  );
};

export default ReporteUsoVenue;
